import { z } from 'zod';

import {
  ActiveElement,
  ArchivedElement,
  HeymergeList,
  HeymergeListElement,
  HeymergeVaultContent,
  HeymergeVaultContentExternal,
  Tombstone,
} from '../../heymerge';
import { schemaISODateString } from '../../isISO8601';
import objectPassthrough from '../../objectPassthrough';
import { ISODateString } from '../../types';
import {
  WithUnknownValuePlaceholder,
  makeUnknownValuePlaceholderSchema,
} from '../../unknownValuePlaceholder';
import { LeakedPasswordCheckInfo, PasswordPolicyCheckInfo } from '../login/Login';
import { LoginUiType, schemaLoginUiType } from '../login/LoginEnums';
import { LoginScopeEntry, UnknownScopeEntry } from '../login/LoginScopeEntry';

interface LoginSummaryData {
  readonly uiType: WithUnknownValuePlaceholder<LoginUiType>;
  readonly username: string;
  readonly scope: WithUnknownValuePlaceholder<LoginScopeEntry, UnknownScopeEntry>[];
  readonly leakedPasswordCheck?: LeakedPasswordCheckInfo;
  readonly passwordPolicyCheck?: PasswordPolicyCheckInfo;
  readonly creationTime: ISODateString;
  readonly version: number;
}

const LoginSummaryData = objectPassthrough({
  uiType: makeUnknownValuePlaceholderSchema(schemaLoginUiType, LoginUiType.login),
  username: z.string(),
  scope: z.array(makeUnknownValuePlaceholderSchema(LoginScopeEntry, { kind: 'unknown' as const })),
  leakedPasswordCheck: LeakedPasswordCheckInfo.optional(),
  passwordPolicyCheck: PasswordPolicyCheckInfo.optional(),
  creationTime: schemaISODateString,
  version: z.number().int(),
});

interface LoginSummaryDataWrapper {
  readonly data: LoginSummaryData;
}

const LoginSummaryDataWrapper = objectPassthrough({
  data: LoginSummaryData,
});

type LoginSummary = HeymergeListElement<LoginSummaryDataWrapper>;

const LoginSummary = z.union([
  LoginSummaryDataWrapper.safeExtend(ActiveElement.shape),
  LoginSummaryDataWrapper.safeExtend(ArchivedElement.shape),
  Tombstone,
]);

interface VaultSummary {
  readonly logins: HeymergeList<LoginSummaryDataWrapper>;
}

const VaultSummary = objectPassthrough({
  logins: z.record(z.string(), LoginSummary),
});

interface OrganizationLoginSummaryVaultContentSpec {
  vaults: VaultSummary;
}

type OrganizationLoginSummaryVaultContent =
  HeymergeVaultContent<OrganizationLoginSummaryVaultContentSpec>;

const VaultSummaryListElement = z.union([
  VaultSummary.safeExtend(ActiveElement.shape),
  VaultSummary.safeExtend(ArchivedElement.shape),
  Tombstone,
]);

const OrganizationLoginSummaryVaultContent = objectPassthrough({
  vaults: z.record(z.string(), VaultSummaryListElement),
});

type OrganizationLoginSummaryVaultContentExternal =
  HeymergeVaultContentExternal<OrganizationLoginSummaryVaultContentSpec>;

const OrganizationLoginSummaryVaultContentExternal = objectPassthrough({
  vaults: z.record(z.string(), VaultSummary),
});

export {
  OrganizationLoginSummaryVaultContent,
  type OrganizationLoginSummaryVaultContentSpec,
  VaultSummary,
  LoginSummaryDataWrapper,
  LoginSummaryData,
  OrganizationLoginSummaryVaultContentExternal,
  LoginSummary,
};
